/*
 * Collection priority for the newbrand PLP (sections/newbrand-plp.liquid).
 *
 *   /collections/<handle>?tag=<tag>   products carrying <tag> shown first
 *   /collections/<handle>?sku=<sku>[,<sku>...]
 *                                     those products first, in the order given,
 *                                     then the products Shopify recommends
 *                                     alongside them (/recommendations, intent
 *                                     "related": purchase history + product
 *                                     data). The recommendations are ONE shared
 *                                     pool -- RECS_TOTAL across the whole list,
 *                                     not RECS_TOTAL per SKU -- filled
 *                                     round-robin so every promoted product is
 *                                     represented before any gets a second pick
 *
 *  - promoted products are removed from their original position, and from any
 *    "Load more" page that arrives later, so nothing shows twice
 *  - promotion is for the landing view only (audit F8, 2026-09-24): once the
 *    shopper filters or sorts, ?tag= / ?sku= are dropped and the plain
 *    filtered/sorted grid shows. A URL that already carries filter.* or
 *    sort_by has them stripped on arrival. "Showing N" counts only this
 *    collection's own products, never the promoted extras
 *  - tag cards come from a section-only render of the native tag route, in this
 *    PLP's own markup, carrying sort_by and filter.* so they obey the shopper's
 *    choices; if this collection holds no product with the tag, the tagged
 *    products from /collections/all are promoted instead; an unknown tag
 *    (Shopify 301s it) promotes nothing
 *  - sku cards are built from the product/recommendations JSON by cloning a
 *    card that is already on the page, so they always match the current card
 *    design; companions may sit in other collections and are shown regardless;
 *    an unknown SKU promotes nothing, and in a list the unknown ones are
 *    dropped while the rest still promote
 *  - loaded from snippets/newbrand-collection-priority.liquid on collection
 *    templates only
 */
(() => {
  const PARAMS = ['tag', 'sku'];
  const MAX_TAG_PAGES = 5;
  const SKUS_MAX = 12; // a ?sku= list longer than this is truncated
  const RECS_TOTAL = 10; // recommendations across the whole list, not per SKU

  const params = () => new URLSearchParams(location.search);
  const activeMode = () => PARAMS.find((p) => params().get(p));
  // The loader snippet hides the grid while a priority parameter is present;
  // reveal it as soon as the order is applied, or after 5s no matter what.
  const reveal = () => document.documentElement.classList.remove('nb-priority-pending');
  const revealTimer = setTimeout(reveal, 5000);
  // Audit F8: a filtered or sorted view is the shopper's own choice, so nothing
  // is promoted on it. The loader snippet strips the params first; this covers
  // the asset loading without it.
  const refined = (q) => [...q.keys()].some((k) => k === 'sort_by' || k.startsWith('filter.'));
  if (activeMode() && refined(params())) {
    const q = params();
    PARAMS.forEach((p) => q.delete(p));
    const qs = q.toString();
    try { history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash); } catch (err) {}
  }
  if (!location.pathname.startsWith('/collections/') || !activeMode()) return reveal();

  const root = document.querySelector('[id^="nb-template--"][id$="newbrand_plp"], [id^="nb-"] .nbl-grid')?.closest('[id^="nb-"]');
  const grid = root?.querySelector('.nbl-grid');
  if (!root || !grid) return reveal();
  const sectionId = root.id.slice(3);
  const handleOf = (card) =>
    card.querySelector('[data-handle]')?.dataset.handle ||
    card.querySelector('a.nbl-title, a.nbl-media')?.getAttribute('href')?.match(/\/products\/([^?#/]+)/)?.[1] ||
    '';
  const collectionPath = () => location.pathname.replace(/\/+$/, '').split('/').slice(0, 3).join('/');

  /* ---- ?tag= / ?sku= are NOT carried into sort or filter (audit F8) ----
     newbrand-plp.liquid's go() drops them from every filter/sort URL, so the
     shopper gets the plain filtered grid without a reload. */

  // The loader snippet (rendered at the top of the PLP) starts the network work
  // before the grid's product cards are parsed. Awaiting its promise here is what
  // keeps the hidden window close to the page's own load time; if it is missing or
  // failed, each mode below still fetches for itself.
  let preCache;
  const prefetched = () => {
    if (preCache === undefined) preCache = Promise.resolve(window.__nbPriority || null).catch(() => null);
    return preCache;
  };
  const cardsInHtml = (html) => [
    ...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.nbl-grid > .nbl-card'),
  ];

  /* ---- tag mode: section-only render of every page of the tag route ---- */
  const taggedCards = async (tag) => {
    const pre = await prefetched();
    if (pre && pre.mode === 'tag' && pre.htmls && pre.htmls.length) {
      const cards = [];
      pre.htmls.forEach((h) => cards.push(...cardsInHtml(h)));
      if (cards.length) return cards;
    }
    // the loader already asked both routes and found nothing
    if (pre && pre.mode === 'tag' && pre.done && pre.htmls && !pre.htmls.length) return [];
    // Tagged products in this collection first; if it holds none, fall back to
    // the whole catalogue so an ad link never lands on a plain grid.
    const own = await taggedCardsFrom(collectionPath(), tag);
    if (own.length || collectionPath() === '/collections/all') return own;
    return taggedCardsFrom('/collections/all', tag);
  };
  const taggedCardsFrom = async (basePath, tag) => {
    const passthrough = [...params()].filter(([k]) => k === 'sort_by' || k.startsWith('filter.'));
    const cards = [];
    for (let page = 1; page <= MAX_TAG_PAGES; page++) {
      const url = new URL(`${basePath}/${encodeURIComponent(tag)}`, location.origin);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', page);
      passthrough.forEach(([k, v]) => url.searchParams.append(k, v));
      // unknown tag 301s to the plain collection; don't download it
      const r = await fetch(url, { credentials: 'same-origin', redirect: 'manual' });
      if (!r.ok || r.redirected || r.type === 'opaqueredirect') break;
      const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      const items = [...doc.querySelectorAll('.nbl-grid > .nbl-card')];
      if (!items.length) break;
      cards.push(...items);
      if (!doc.querySelector('[data-nbl-next]')) break;
    }
    return cards;
  };

  /* ---- sku mode: product + recommendations, rendered by cloning a live card ---- */
  const moneyFormat = () => {
    const sample = grid.querySelector('.nbl-now')?.textContent.trim() || '';
    const m = sample.match(/^([^0-9]*)[0-9.,\s]+([^0-9]*)$/);
    const pre = m ? m[1] : 'Rs. ';
    const suf = m ? m[2] : '';
    return (paise) => pre + Math.round(paise / 100).toLocaleString('en-IN') + suf;
  };
  const brandHandleFor = (vendor) =>
    [...grid.querySelectorAll('.nbl-brand[data-b]')].find((b) => b.textContent.trim() === vendor)?.dataset.b;

  // The PLP shows product.metafields.magento.brand, which the JSON endpoints do
  // not expose. The Magento category tags carry it: "Category_Root Catalog/<Brand> - ...".
  // Prefer a brand already named on this page (so its data-b handle can be
  // reused), then the tag itself, then the vendor unless it is the legal entity.
  const brandOf = (p) => {
    const tags = (p.tags || []).map((t) => String(t));
    const known = [...new Set([...grid.querySelectorAll('.nbl-brand')].map((b) => b.textContent.trim()))];
    const hit = known.find((name) => name && tags.some((t) => t.includes(name)));
    if (hit) return hit;
    for (const t of tags) {
      const m = t.match(/^Category_Root Catalog\/([^/]+?)(?: - |\/|$)/);
      if (m && !/^jewell?ery$/i.test(m[1])) return m[1].trim();
    }
    const vendor = p.vendor || '';
    return /private limited|pvt/i.test(vendor) ? '' : vendor;
  };

  // 2026-09-22 (PLP-PDP-SPEED items 2 + 6): the cloned card gets the same
  // srcset widths as the Liquid cards, and only the first few load eagerly.
  const WIDTHS = [240, 360, 480, 600, 800, 1000];
  const EAGER = 4;
  const sized = (image, w) => image + (image.includes('?') ? '&' : '?') + 'width=' + w;
  const buildCard = (template, p, money, index = EAGER) => {
    const card = template.cloneNode(true);
    // 2026-09-24 audit (code-plp 9): the first variant that can be bought, as
    // the Liquid cards use (selected_or_first_available_variant); variants[0]
    // put a sold-out variant in the bag form. Its price, not the product's
    // lowest, so the clone reads like the Liquid card.
    const v = p.variants?.find((x) => x && x.available) || p.variants?.[0] || {};
    const price = Number(v.id ? v.price : (p.price ?? 0)) || 0;
    const was = Number(v.id ? v.compare_at_price : p.compare_at_price) || 0;
    const onSale = was > price;
    const url = `/products/${p.handle}`;
    const image = p.featured_image || p.images?.[0] || '';
    const imgSrc = image ? sized(image, 600) : '';

    const media = card.querySelector('a.nbl-media');
    if (media) media.setAttribute('href', url);
    const img = card.querySelector('.nbl-media img');
    if (img) {
      img.removeAttribute('fetchpriority');
      img.loading = index < EAGER ? 'eager' : 'lazy';
      if (image) img.srcset = WIDTHS.map((w) => sized(image, w) + ' ' + w + 'w').join(', ');
      else img.removeAttribute('srcset');
      img.src = imgSrc;
      img.alt = p.title || '';
      img.removeAttribute('width');
      img.removeAttribute('height');
    }
    card.querySelector('.nbl-badge')?.remove();
    // The template is somebody else's card: its Anti-tarnish badge belongs to
    // that product, not this one. Drop it here and let paintAntiTarnish() put
    // the right one back from the product's own metafield.
    card.querySelector('.nbl-atb')?.remove();
    card.setAttribute('data-nb-cloned', '');
    if (onSale && media) {
      const off = Math.round(((was - price) * 100) / was);
      const badge = document.createElement('span');
      badge.className = 'nbl-badge';
      badge.innerHTML = `<i>${off}% OFF</i><em>Sale</em>`;
      media.appendChild(badge);
    }

    const idInput = card.querySelector('form input[name="id"]');
    if (idInput) idInput.value = v.id || '';
    const addBtn = card.querySelector('form .nbl-act');
    if (addBtn) {
      addBtn.classList.remove('is-incart');
      addBtn.removeAttribute('data-nb-opencart');
      addBtn.setAttribute('aria-label', 'Add to cart');
      addBtn.disabled = !(p.available ?? v.available ?? true);
    }
    const wish = card.querySelector('[data-nb-wish]');
    if (wish) {
      wish.dataset.pid = p.id;
      wish.dataset.vid = v.id || '';
      wish.dataset.handle = p.handle;
    }

    const brand = card.querySelector('.nbl-brand');
    const vendor = brandOf(p);
    if (brand) {
      if (vendor) {
        brand.textContent = vendor;
        const bh = brandHandleFor(vendor);
        if (bh) brand.dataset.b = bh;
        else brand.removeAttribute('data-b');
      } else brand.remove();
    }
    const title = card.querySelector('a.nbl-title');
    if (title) {
      title.textContent = p.title || '';
      title.setAttribute('href', url);
    }
    const now = card.querySelector('.nbl-now');
    if (now) now.textContent = money(price);
    card.querySelector('.nbl-was')?.remove();
    if (onSale && now) {
      const wasEl = document.createElement('span');
      wasEl.className = 'nbl-was';
      wasEl.textContent = money(was);
      now.after(wasEl);
    }
    const stock = card.querySelector('.nbl-stock');
    if (stock) {
      const ok = p.available ?? v.available ?? true;
      stock.classList.toggle('is-out', !ok);
      stock.textContent = ok ? 'In stock' : 'Out of stock';
    }
    return card;
  };

  // Anti-tarnish is a metafield, and neither /products/<handle>.js nor the
  // recommendations endpoint carries metafields. templates/product.newbrand-atb
  // answers "1" or "0" for one product; ask it once per handle and cache.
  // Anything that fails leaves the card without a badge, as it already is.
  const atbCache = new Map();
  const antiTarnish = (handle) => {
    if (!atbCache.has(handle)) {
      atbCache.set(
        handle,
        fetch(`/products/${encodeURIComponent(handle)}?view=newbrand-atb`, {
          credentials: 'same-origin',
        })
          .then((r) => (r.ok ? r.text() : ''))
          .then((t) => t.trim() === '1')
          .catch(() => false)
      );
    }
    return atbCache.get(handle);
  };
  const paintAntiTarnish = (nodes) => {
    nodes.forEach((node) => {
      const handle = handleOf(node);
      const media = node.querySelector('a.nbl-media');
      if (!handle || !media) return;
      antiTarnish(handle).then((on) => {
        if (!on || !media.isConnected || media.querySelector('.nbl-atb')) return;
        const b = document.createElement('span');
        b.className = 'nbl-atb';
        b.textContent = 'Anti-tarnish';
        media.appendChild(b);
      });
    });
  };

  const skuHandles = (raw) =>
    [...new Set(raw.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean))].slice(0, SKUS_MAX);
  const getProduct = async (h) => {
    const r = await fetch(`/products/${encodeURIComponent(h)}.js`, {
      headers: { Accept: 'application/json' },
    }).catch(() => null);
    return r && r.ok ? r.json().catch(() => null) : null;
  };
  const getRecs = async (id, limit) => {
    const r = await fetch(
      `/recommendations/products.json?product_id=${id}&limit=${limit}&intent=related`
    ).catch(() => null);
    if (!r || !r.ok) return [];
    const d = await r.json().catch(() => null);
    return (d && d.products) || [];
  };

  // Every listed SKU first, in the order given, then one shared pool of
  // recommendations. Each SKU contributes its own, taken round-robin and
  // de-duplicated against the promoted products and each other, so a list of
  // three SKUs still yields RECS_TOTAL companions in total rather than 3 x 10.
  const skuOrder = async (raw) => {
    const found = (await Promise.all(skuHandles(raw).map(getProduct))).filter(Boolean);
    if (!found.length) return [];
    const seen = new Set(found.map((p) => p.handle));
    // Shopify caps a recommendations call at 10. Ask each SKU for only the
    // share it can contribute, plus a couple to survive de-duplication.
    const heads = found.slice(0, RECS_TOTAL);
    const per = Math.min(10, Math.ceil(RECS_TOTAL / heads.length) + 2);
    const lists = await Promise.all(heads.map((p) => getRecs(p.id, per)));
    const recs = [];
    for (let i = 0; recs.length < RECS_TOTAL && lists.some((l) => i < l.length); i++) {
      for (const l of lists) {
        if (recs.length >= RECS_TOTAL) break;
        const p = l[i];
        if (p && !seen.has(p.handle)) {
          seen.add(p.handle);
          recs.push(p);
        }
      }
    }
    return [...found, ...recs];
  };

  const skuProducts = async (raw) => {
    const pre = await prefetched();
    // pre.done means the loader snippet ran to completion, so an empty list is
    // a real answer (every SKU unknown) and must not be fetched a second time.
    return pre && pre.mode === 'sku' && Array.isArray(pre.ordered) && (pre.ordered.length || pre.done)
      ? pre.ordered
      : skuOrder(raw);
  };
  const skuCards = async (raw) => {
    const tpl = grid.querySelector('.nbl-card');
    if (!tpl) return [];
    const ordered = await skuProducts(raw);
    if (!ordered.length) return [];
    const money = moneyFormat();
    return ordered.map((p, i) => buildCard(tpl, p, money, i));
  };

  /* ---- apply: remove originals, prepend, keep later pages clean ---- */
  const cache = new Map();
  const promotedCards = () => {
    const mode = activeMode();
    const value = params().get(mode);
    const key = `${mode}|${value}|${location.search}`;
    if (!cache.has(key)) cache.set(key, mode === 'tag' ? taggedCards(value) : skuCards(value));
    return cache.get(key);
  };
  const promotedHandles = new Set();
  let applying = false;
  // Products of this collection that were moved to the top, or dropped from a
  // later page because they are already on top. "Showing N" counts these plus
  // the unpromoted cards: SKU companions from other collections are extras.
  const replaced = new Set();

  const updateCount = () => {
    const head = root.querySelector('.nbl-count b');
    if (head) head.textContent = grid.querySelectorAll(':scope > .nbl-card:not([data-nb-promoted])').length + replaced.size;
  };

  const apply = (cards, after) => {
    // the shopper filtered or sorted before this arrived: that grid is theirs
    if (!cards.length || !activeMode() || !grid.isConnected) return;
    applying = true;
    cards.forEach((c) => promotedHandles.add(handleOf(c)));
    // originals only: in SKU mode apply() runs twice, and the second pass must
    // not take out the products the first one promoted
    [...grid.querySelectorAll(':scope > .nbl-card:not([data-nb-promoted])')].forEach((c) => {
      if (promotedHandles.has(handleOf(c))) {
        replaced.add(handleOf(c));
        c.remove();
      }
    });
    const fragment = document.createDocumentFragment();
    cards.forEach((c) => {
      const node = document.importNode(c, true);
      node.setAttribute('data-nb-promoted', '');
      fragment.appendChild(node);
    });
    const added = [...fragment.children];
    if (after && after.parentNode === grid) after.after(fragment);
    else grid.prepend(fragment);
    paintAntiTarnish(added.filter((n) => n.hasAttribute('data-nb-cloned')));
    updateCount();
    applying = false;
  };

  // "Load more" appends further pages; drop any promoted product arriving again.
  // (Observer callbacks run after apply() has returned, so the promoted cards
  // are recognised by their attribute, not by the applying flag.)
  new MutationObserver((mutations) => {
    // after a filter/sort the params are gone and this grid is the plain one
    if (!promotedHandles.size || !activeMode()) return;
    let removed = false;
    mutations.forEach((m) =>
      m.addedNodes.forEach((n) => {
        if (
          n.nodeType === 1 &&
          n.classList.contains('nbl-card') &&
          !n.hasAttribute('data-nb-promoted') &&
          promotedHandles.has(handleOf(n))
        ) {
          replaced.add(handleOf(n));
          n.remove();
          removed = true;
        }
      })
    );
    if (removed) updateCount();
  }).observe(grid, { childList: true });

  // SKU mode in two steps (PLP-PDP-SPEED item 6): the promoted products are
  // shown, and the grid revealed, as soon as they arrive; the recommendations
  // follow straight after them when their own round trip finishes. The loader
  // publishes the products alone as window.__nbPriorityHeads. Without it this
  // falls back to the one-step path.
  const runSku = async () => {
    const raw = params().get('sku');
    const heads = await Promise.resolve(window.__nbPriorityHeads || null).catch(() => null);
    const tpl = grid.querySelector('.nbl-card');
    if (!heads || !tpl) return apply(await promotedCards());
    if (!heads.length) return; // every SKU unknown: nothing to promote
    const money = moneyFormat();
    apply(heads.map((p, i) => buildCard(tpl, p, money, i)));
    clearTimeout(revealTimer);
    reveal();
    const shown = new Set(heads.map((p) => p.handle));
    const rest = (await skuProducts(raw)).filter((p) => !shown.has(p.handle));
    const last = [...grid.querySelectorAll(':scope > [data-nb-promoted]')].pop();
    apply(rest.map((p, i) => buildCard(tpl, p, money, heads.length + i)), last);
  };

  const run = async () => {
    try {
      if (activeMode() === 'sku') await runSku();
      else apply(await promotedCards());
    } catch (error) {
      console.error('[collection-priority]', error);
    } finally {
      clearTimeout(revealTimer);
      reveal();
    }
  };
  // Start straight away: the fetches are the slow part, and waiting for idle
  // time only lengthens the hidden period.
  run();
})();
